import React from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { apiCompanies } from '~constants/api';
import { AuthTags } from '~constants/authTags';
import { AppointmentStatuses } from '~constants/maps';
import { tponeUrl } from '~constants/tpone';

import { Appointment } from '~weekly-planner/lib/common';

import { Button } from '~components';
import ContainerBanner from '~components/ContainerBanner';
import ContainerHeader from '~components/ContainerHeader';
import ErrorResponse from '~components/ErrorResponse';
import Icon, {
  faArrowLeft,
  faBan,
  faInfoCircle,
  faLock,
  faPhoneSlash,
  faPlus,
  faTriangleExclamation,
} from '~components/icon';
import Link from '~components/Link';
import CaseManagementSettings from '~appointments/components/CaseManagementSettings';
import ExportToPDFButton from '~appointments/components/ExportToPDFButton';

import { notifyUser } from '~appointments/actions/appointments';
import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getCareWorkers } from '~care-workers/actions';
import { getAll as getClients } from '~main/actions/clients';

import { clearMessage, clearNotifications, copyAppointment } from '~appointments/reducers/appointments';

import {
  selectAppointmentMessage,
  selectAppointmentNotification,
  selectCurrentAppointment,
  selectErrorResponse,
} from '~appointments/selectors/appointments';
import { selectAuthenticatedUser, selectUserTags } from '~main/selectors/login';

interface AppointmentBannerProps {
  headerClassName: string;
  headerText: string;
  dataCy: string;
  iconColor?: string;
  formDefaults?: any;

  // Additional display flags
  showRepeatLabel?: boolean;
  showTransportLabel?: boolean;
  showCancellationMessage?: boolean;
  isNew?: boolean;
  isCaseManagement?: boolean;

  // For navigation handling
  onBackNavigatesTo?: 'appointments' | 'repeats' | 'groups' | string;
}

const AppointmentBanner: React.FC<AppointmentBannerProps> = ({
  headerClassName,
  headerText,
  dataCy,
  iconColor,
  formDefaults,
  showRepeatLabel = false,
  showTransportLabel = false,
  showCancellationMessage = false,
  isNew = false,
  onBackNavigatesTo = 'appointments',
  isCaseManagement = false,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { id, group_id } = useParams();

  const appointment = useAppSelector(selectCurrentAppointment);
  const errorResponse = useAppSelector(selectErrorResponse);
  const message = useAppSelector(selectAppointmentMessage);
  const { success: notificationSuccess, allow_override, reason } = useAppSelector(selectAppointmentNotification) ?? {};
  const tags = useAppSelector(selectUserTags);
  const user = useAppSelector(selectAuthenticatedUser);

  const messages = [];

  const REDIRECT_URL = tponeUrl;
  const parentExists = appointment?.parent_repeat_id != null && appointment?.parent_repeat_id > 0;
  const parent = appointment?.parent_repeat;

  if (appointment?.status_id === AppointmentStatuses.STARTED) messages.push('Appointment has already started');
  if (appointment?.declined) messages.push('Appointment was declined');

  const authorised = tags.includes(AuthTags.APPOINTMENTS.UPDATE);

  const onBack = () => {
    switch (onBackNavigatesTo) {
      case 'appointments':
        navigate('/appointments');
        break;
      case 'repeats':
        navigate('/appointments/repeats');
        break;
      case 'groups':
        navigate('/appointments/groups');
        break;
      default:
        navigate('/appointments');
        break;
    }
  };

  const getExportedMessages = () => {
    const messages = [];
    if (appointment?.exported_payroll) messages.push('Exported to Payroll');
    if (appointment?.exported_invoices) messages.push('Exported to Invoice');
    return messages.map((message) => `${message} - Only accounts users can alter this appointment`);
  };

  const copy = async (link: string) => {
    // Copy appointment into reducer
    await dispatch(getLocations());
    await dispatch(getServiceTypes());
    await dispatch(getCareWorkers());
    await dispatch(getClients());
    dispatch(copyAppointment(appointment));

    // Navigate to link
    navigate(link);
  };

  const notify = () => {
    dispatch(clearNotifications());
    dispatch(notifyUser({ id, override: true }));
  };

  const exportMessages = getExportedMessages();

  const showClientAppointmentsNav = !isNew && authorised;

  const smsMessages: any[] = [`SMS not sent - ${reason}`];
  if (allow_override === true) {
    smsMessages.push(
      <Button key={'override'} className="mt-3" onClick={notify}>
        Force Send
      </Button>,
    );
  }

  const canEditCaseManagementSettings =
    tags?.includes(AuthTags.APPOINTMENTS.CASEMANAGEMENT.SETTINGS) && isCaseManagement;

  const getCreationPath = () => {
    switch (true) {
      case appointment?.repeat:
        return '/appointments/repeats/new';
      case appointment?.case_management:
        return '/appointments/case-management/new';
      default:
        return '/appointments/new';
    }
  };
  return (
    <>
      <ContainerHeader
        dataCy={dataCy}
        className={headerClassName}
        settingsElement={
          canEditCaseManagementSettings && (
            <CaseManagementSettings
              key="case-management-settings-icon"
              formDefaults={formDefaults}
              iconColor={iconColor}
            />
          )
        }
        actionButtons={[
          <ExportToPDFButton
            key="export-pdf-button"
            appointment={appointment as Appointment}
            logo={`${apiCompanies}/${user?.company?.id}/logo`}
            company={user?.company}
            color={iconColor}
          />,
        ]}
      >
        {showRepeatLabel && <span className="me-1">Repeat</span>}
        {showTransportLabel && <span className="me-1">Transport</span>}
        <span>{headerText}</span>
      </ContainerHeader>

      <div className="d-flex justify-content-between bg-light p-2 mt-2 mb-2">
        <div>
          {appointment?.declined && (
            <Link to={`${REDIRECT_URL}/redirect-url.asp?redirectArg=declined`}>
              <Button
                size="sm"
                color="danger"
                className="me-2 nav-element"
                data-cy={'appointment-back-to-declined-button'}
              >
                <Icon className="me-2" icon={faArrowLeft} />
                Back to Declined Appointments
              </Button>
            </Link>
          )}
          <Button
            size="sm"
            color="success"
            className="me-2 nav-element"
            onClick={() => onBack()}
            data-cy={'appointment-back-to-list-button'}
          >
            <Icon className="me-2" icon={faArrowLeft} />
            Back to Appointments
          </Button>
          {!isNew && (
            <Link to={`${REDIRECT_URL}/client-details.asp?eid=${appointment?.client_id}`}>
              <Button
                size="sm"
                color="success"
                className="me-2 nav-element"
                disabled={isNaN(parseInt(appointment?.client_id))}
                data-cy={'appointment-back-to-client-details-button'}
              >
                <Icon className="me-2" icon={faArrowLeft} />
                Back to Client Details
              </Button>
            </Link>
          )}
          {showClientAppointmentsNav && (
            <Link
              to={`${REDIRECT_URL}/client-details.asp?eid=${appointment?.client_id}&BREAKDOWN_SHOW_APPTS=yes&wide1=yes`}
            >
              <Button
                size="sm"
                color="success"
                className="me-2 nav-element"
                disabled={isNaN(parseInt(appointment?.client_id))}
                data-cy={'appointment-back-to-client-appointments-button'}
              >
                <Icon className="me-2" icon={faArrowLeft} />
                Back to Client Appointments
              </Button>
            </Link>
          )}
        </div>
        <div>
          {authorised && (
            <>
              {appointment?.group_session_id === 1 && (
                <>
                  <Button
                    size="sm"
                    color="success"
                    className="ms-2 me-2"
                    disabled={isNew}
                    onClick={() => copy(`/appointments/groups${showRepeatLabel ? '/repeats' : ''}/${id}/users/new`)}
                    data-cy={'appointment-add-user-button'}
                  >
                    <Icon className="me-2" icon={faPlus} />
                    Add Care Worker
                  </Button>
                  <span>/</span>
                  <Button
                    size="sm"
                    color="success"
                    className="ms-2 me-2"
                    disabled={isNew}
                    onClick={() => copy(`/appointments/groups${showRepeatLabel ? '/repeats' : ''}/${id}/clients/new`)}
                    data-cy={'appointment-add-client-button'}
                  >
                    <Icon className="me-2" icon={faPlus} />
                    Add Client
                  </Button>
                </>
              )}
              <span>Create Similar:</span>
              {!showRepeatLabel && (
                <>
                  <Button
                    size="sm"
                    color="success"
                    className="ms-2 me-2"
                    disabled={isNew || !appointment}
                    onClick={() => copy(getCreationPath())}
                    data-cy={'appointment-create-button'}
                  >
                    <Icon className="me-2" icon={faPlus} />
                    Appointment
                  </Button>
                  <span>/</span>{' '}
                </>
              )}

              <Button
                size="sm"
                color="warning"
                className="ms-2 me-2"
                disabled={isNew || !appointment}
                onClick={() => copy('/appointments/repeats/new')}
                data-cy={'appointment-create-repeat-button'}
              >
                <Icon className="me-2" icon={faPlus} />
                Repeat
              </Button>
            </>
          )}
          <Button
            authTag={AuthTags.APPOINTMENTS.CANCEL}
            size="sm"
            color="danger"
            className="me-2"
            disabled={isNew || !appointment}
            onClick={() => navigate(`/appointments/${id}/cancel`)}
            data-cy={'appointment-cancel-button'}
          >
            <Icon className="me-2" icon={faBan} />
            Cancel Appointment
          </Button>
        </div>
      </div>
      {exportMessages.length > 0 && (
        <ContainerBanner
          className="m-2"
          color="info"
          icon={faLock}
          iconSize="1x"
          messages={exportMessages}
          dataCy="appointment-export-message-banner"
        />
      )}
      {message && (
        <ContainerBanner
          className="m-2 lead"
          color="success"
          textColor="white"
          icon={faInfoCircle}
          iconSize="1x"
          messages={[message]}
          onClear={() => dispatch(clearMessage())}
          dataCy="appointment-notification-message-banner"
        />
      )}
      {messages.length > 0 && (
        <ContainerBanner className="m-2" color="info" icon={faInfoCircle} iconSize="2x" messages={messages} />
      )}
      {showCancellationMessage && (
        <ContainerBanner
          className="mt-3 mb-3"
          color="danger"
          textColor="white"
          icon={faTriangleExclamation}
          iconSize="2x"
          messages={['Are you sure you wish to cancel this appointment?']}
          dataCy="appointment-cancellation-message-banner"
        />
      )}
      {notificationSuccess === false && (
        <ContainerBanner
          className="m-2"
          color="warning"
          textColor="white"
          icon={faPhoneSlash}
          iconSize="1x"
          messages={smsMessages}
          onClear={() => dispatch(clearNotifications())}
          dataCy="appointment-sms-message-banner"
        />
      )}
      {appointment && <ErrorResponse message={errorResponse} dataCy={'appointment-errorResponse'} />}
      {parentExists && authorised && (
        <div
          data-cy="appointment-view-repeat-series-button"
          className="m-2 p-2 border-2 border-top border-bottom border-warning selectable"
          onClick={() => navigate(`/appointments/${parent?.id}`)}
        >
          {`This appointment is part of a repeat series. Click to view repeat appointment #${parent?.reference_number}`}
        </div>
      )}
      {appointment?.group_session_id > 1 && (
        <>
          <div
            data-cy="appointment-view-group-session-button"
            className="m-2 p-2 border-2 border-top border-bottom border-success selectable"
            onClick={() => navigate(`/appointments/${appointment?.group_session_id}`)}
          >
            {`This appointment is a part of group session #${appointment?.group_session_id}.`}
          </div>
        </>
      )}
      {group_id && (
        <>
          <div
            data-cy="appointment-create-group-session-button"
            className="m-2 p-2 border-2 border-top border-bottom border-success selectable"
            onClick={() => navigate(`/appointments/${group_id}`)}
          >
            {`This appointment will be a part of group session #${group_id}.`}
          </div>
        </>
      )}
    </>
  );
};

export default AppointmentBanner;
